// src/app/home/page.jsx
import React from "react";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { ArrowRight, ShoppingBag, Sparkles, Palette, Calendar, Package, Gift } from "lucide-react";
import { LogoCrest } from "../../components/Logo";

const features = [
  {
    title: "Product Catalog",
    description: "Browse our artisan collection of flowers, candles, soaps, and decor.",
    href: "products",
    icon: ShoppingBag,
    gradient: "from-primary to-accent",
  },
  {
    title: "Customization Studio",
    description: "Personalize every detail of your event products.",
    href: "customization",
    icon: Sparkles,
    gradient: "from-primary to-accent",
  },
  {
    title: "Mood Board AI",
    description: "Visualize your event with AI-powered mood boards.",
    href: "mood-board",
    icon: Palette,
    gradient: "from-primary to-accent",
  },
  {
    title: "Book a Consultation",
    description: "Let our experts help plan your perfect event.",
    href: "booking",
    icon: Calendar,
    gradient: "from-primary to-accent",
  },
  {
    title: "Subscription Boxes",
    description: "Curated seasonal delights delivered monthly.",
    href: "subscription",
    icon: Package,
    gradient: "from-primary to-accent",
  },
  {
    title: "Gift Registry",
    description: "Create and share your wishlist with loved ones.",
    href: "registry",
    icon: Gift,
    gradient: "from-primary to-accent",
  },
];

export default function HomePage() {
  const navigate = (href) => {
    window.location.hash = `#/${href}`;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Glass Morphism */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/80 via-background to-accent/50 opacity-60">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxIDEuNzktNCAzLjk5LTRDNC4yMSAzMCA2IDMxLjc5IDYgMzRjMCAyLjIxIDEuNzkgNCAzLjk5IDRDMTkuMjEgMzggMjEgMzYuMjEgMjEgMzRjMC0yLjIxIDEuNzktNCAzLjk5LTRDNC4yMSAzMCA2IDMxLjc5IDYgMzR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
        </div>

        {/* Glass Card */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="backdrop-blur-xl bg-background/30 rounded-3xl p-12 border border-border/40 shadow-2xl">
            {/* Logo Crest */}
            <div className="flex justify-center mb-6">
              <LogoCrest className="h-28 md:h-36 w-auto" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 font-headline tracking-wider"
                style={{
                  background: 'linear-gradient(135deg, #C9A962 0%, #E5D4A1 50%, #C9A962 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
              AURUM & IVY
            </h1>
            <p className="text-2xl md:text-3xl text-foreground mb-4 font-light">
              Artisan Events & Curated Goods
            </p>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Elevate your celebrations with handcrafted beauty, personalized touches, and unforgettable experiences
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/30 text-lg px-8"
                onClick={() => navigate('products')}
              >
                Explore Collections
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-border/60 backdrop-blur-sm bg-background/40 hover:bg-background/60 text-foreground text-lg px-8"
                onClick={() => navigate('booking')}
              >
                Book Consultation
              </Button>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary/20 rounded-full opacity-30 blur-xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-accent/30 rounded-full opacity-30 blur-xl animate-float-delay"></div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Our Services
            </h2>
            <p className="text-xl text-muted-foreground">
              Everything you need to create the perfect event
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={feature.href}
                  className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-border cursor-pointer backdrop-blur-sm bg-card/70 overflow-hidden"
                  onClick={() => navigate(feature.href)}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                  <CardHeader className="relative">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <Icon className="h-7 w-7 text-primary-foreground" />
                    </div>
                    <CardTitle className="text-2xl font-bold group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-accent group-hover:bg-clip-text transition-all duration-300">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="relative">
                    <p className="text-muted-foreground mb-4">
                      {feature.description}
                    </p>
                    <div className="flex items-center text-primary font-semibold group-hover:translate-x-2 transition-transform duration-300">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-accent to-secondary"></div>
            <div className="relative z-10 backdrop-blur-sm bg-background/10 p-12 text-center text-primary-foreground">
              <h2 className="text-4xl font-bold mb-4">Ready to Create Something Amazing?</h2>
              <p className="text-xl mb-8 opacity-90">
                Let's bring your vision to life with our artisan touch
              </p>
              <Button
                size="lg"
                className="bg-background text-primary hover:bg-background/90 text-lg px-8 shadow-xl"
                onClick={() => navigate('booking')}
              >
                Start Planning
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delay {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-30px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delay {
          animation: float-delay 8s ease-in-out infinite;
        }
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 5s ease infinite;
        }
      `}</style>
    </div>
  );
}
